import Markdown from '@visma/formula/lib/Markdown';
import { Typography } from '@material-ui/core';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';
import sift from 'sift';
import {
  isNotAnswered,
  cellAnswerTypes,
  rowAnswerTypes,
  formElementTypes,
  noVisibleTypes,
  dataValueFlat,
} from './utils';
import DataValue from './DataValue';

import { LAYER } from './index';

const PrintableTableCell = ({ children, ...props }) => {
  return (
    <TableCell className="print-small-padding" {...props}>
      {children}
    </TableCell>
  );
};

export default function DataRow({
  element,
  allElements,
  formData,
  hideNotAnswered,
  fullFormData,
  useLayers,
}) {
  if (useLayers && element.layer > LAYER) {
    return null;
  }

  // Elementit, kuten leipäteksti, väliotsikot jne.
  if (formElementTypes.includes(element.type)) {
    const content = element.content ? JSON.parse(element.content) : null;
    let cellContent;
    let noPrint = false;

    switch (element.type) {
      case 'pageTitle':
        cellContent = (
          <Typography className="print-h5" variant="h4" component="h2">
            <b>{content?.fi}</b>
          </Typography>
        );
        break;
      case 'subtitle':
        cellContent = <Markdown>{content?.fi}</Markdown>;
        break;
      case 'body':
        noPrint = true;
        cellContent = <Markdown>{content?.fi}</Markdown>;
        break;
      case 'image':
        cellContent = <img src={element.url} />;
        noPrint = true;
        break;
      case 'title':
        cellContent = (
          <Typography className="print-h5" variant="h4" component="h2">
            {content?.fi}
          </Typography>
        );
        break;
    }

    return cellContent ? (
      <TableRow
        {...(noPrint ? { className: 'no-print-element' } : {})}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <PrintableTableCell colSpan={2}>{cellContent}</PrintableTableCell>
      </TableRow>
    ) : (
      <></>
    );
  }

  // Onko elementti piilotettu tai vastaus tyhjä
  if (element.hidden || (hideNotAnswered && isNotAnswered(element, formData))) {
    return <></>;
  }

  if (element.multiValued) {
    // Toistettavat kysymykset
    if (cellAnswerTypes.includes(element.type)) {

      if(element.hidden || (!hideNotAnswered && !formData)) {
        return <></>;
      }

      return (
        <>
          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <PrintableTableCell rowSpan={formData && formData.length > 0 ? formData.length : null} width={'55%'}>
              {element?.messages?.fi?.title}
            </PrintableTableCell>
            <PrintableTableCell width={'45%'}>
              <b>
                <DataValue formData={formData ? formData[0] : undefined} element={element} />
              </b>
            </PrintableTableCell>
          </TableRow>
          {formData && formData.slice(1).map((data, i) => {
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                key={i}
              >
                <PrintableTableCell width={'45%'}>
                  <b>
                    <DataValue formData={data} element={element} />
                  </b>
                </PrintableTableCell>
              </TableRow>
            );
          })}
        </>
      );
    }

    // Toistettavaa monivalintaa ei tavallisesti käytetä mihinkään, mutta lisätään erikoiskäsittely varmuuden vuoksi
    // Ei tueta korostusta toistettavilla monivalintakysymyksillä
    if (element.type === 'multiselect') {
      const values = formData.map((data) => dataValueFlat(element, data));
      return (
        <DataRow formData={values} element={{ ...element, type: 'text' }} />
      );
    }

    // Toistettavat kysymykset joiden vastauksille varataan kokonainen rivi (esim. monirivinen tekstikenttä)
    if (rowAnswerTypes.includes(element.type)) {
      return (
        <>
          {element?.messages?.fi?.title && (
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <PrintableTableCell sx={{ borderBottom: 'none' }} colSpan={2}>
                {element.messages.fi.title}
              </PrintableTableCell>
            </TableRow>
          )}
          {formData.slice(0, -1).map((data) => {
            return (
              <TableRow>
                <PrintableTableCell
                  sx={{ borderBottom: 'none' }}
                  style={{ paddingLeft: 50, whiteSpace: 'pre-line' }}
                  colSpan={3}
                >
                  <b>
                    <DataValue formData={data} element={element} />
                  </b>
                </PrintableTableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <PrintableTableCell
              sx={{ borderTop: 'none' }}
              style={{ paddingLeft: 50, whiteSpace: 'pre-line' }}
              colSpan={3}
            >
              <b>
                <DataValue
                  formData={formData[formData.length - 1]}
                  element={element}
                />
              </b>
            </PrintableTableCell>
          </TableRow>
        </>
      );
    }

    // Toistettava kysymysryhmä
    if (element.elements) {
      const elements = element.elements
        .filter((el) => (element.useLayers ? LAYER >= el.layer : true))
        .sort((el1, el2) => el1.orderNum - el2.orderNum);
      return (
        <>
          {element?.messages?.fi?.title && (
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <PrintableTableCell sx={{ borderBottom: 'none' }} colSpan={2}>
                {element.messages.fi.title}
              </PrintableTableCell>
            </TableRow>
          )}
          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <PrintableTableCell colSpan={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {elements.map((el, i) => (
                      <TableCell key={i}>
                        <b>{el.messages?.fi?.title}</b>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData?.map((data, i) => (
                    <TableRow key={i}>
                      {elements.map((el, x) => {
                        return (
                          <TableCell key={x}>
                            {el.multiValued ? (
                              data[el.name]
                                .map((d) => dataValueFlat(el, d))
                                .join(', ')
                            ) : (
                              <DataValue
                                formData={data[el.name]}
                                element={el}
                                key={x}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </PrintableTableCell>
          </TableRow>
        </>
      );
    }

    return null;
  }

  if (element.type === 'multiselect') {
    return (
      <DataRow
        element={{
          ...element,
          filter: '{}',
          multiValued: true,
          type: 'select',
        }}
        formData={formData}
      />
    );
  }

  // Tavalliset kysymykset
  if (cellAnswerTypes.includes(element.type)) {
    if (element.messages?.fi?.title && element.messages?.fi?.title !== '') {
      return (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <PrintableTableCell width={'55%'}>
            {element.messages.fi.title}
          </PrintableTableCell>
          <PrintableTableCell width={'45%'}>
            <b>
              <DataValue formData={formData} element={element} />
            </b>
          </PrintableTableCell>
        </TableRow>
      );
    } else {
      //Jos kysymyksellä ei ole näkyvää otsikkoa, palauta pelkästään data yhdellä rivillä
      return (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <PrintableTableCell style={{ paddingLeft: 50 }} colSpan={2}>
            <b>
              <DataValue formData={formData} element={element} />
            </b>
          </PrintableTableCell>
        </TableRow>
      );
    }
  }

  // Kysymykset joiden vastauksille varataan kokonainen rivi (esim. monirivinen tekstikenttä)
  if (rowAnswerTypes.includes(element.type)) {
    return (
      <>
        {element.messages?.fi?.title && element.messages?.fi?.title !== '' ? (
          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <PrintableTableCell sx={{ borderBottom: 'none' }} colSpan={2}>
              {element.messages.fi.title}
            </PrintableTableCell>
          </TableRow>
        ) : (
          <></>
        )}
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <PrintableTableCell
            style={{ paddingLeft: 50, whiteSpace: 'pre-line' }}
            colSpan={2}
          >
            <b>
              <DataValue formData={formData} element={element} />
            </b>
          </PrintableTableCell>
        </TableRow>
      </>
    );
  }

  // Tavallinen kysymysryhmä
  if (element.elements) {
    const elements = element.elements.sort(
      (el1, el2) => el1.orderNum - el2.orderNum
    );
    return (
      <>
        {element.messages?.fi?.title && element.messages?.fi?.title !== '' ? (
          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <PrintableTableCell colSpan={2}>
              <Typography
                className="print-h3"
                style={{ fontSize: 20 }}
                variant="h4"
              >
                {element.messages.fi.title}
              </Typography>
            </PrintableTableCell>
          </TableRow>
        ) : (
          <></>
        )}
        {elements.map((el, i) => (
          <DataRow
            element={{
              ...el,
              handleFalseAsUndefined: element.handleFalseAsUndefined,
            }}
            allElements={elements}
            formData={formData ? formData[el.name] : undefined}
            fullFormData={formData}
            hideNotAnswered={hideNotAnswered}
            useLayers={element.useLayers}
            key={i}
          />
        ))}
      </>
    );
  }

  // Fallback tyypeille, joilla ei ole käsittelyä
  if (formData !== undefined && !noVisibleTypes.includes(element.type)) {
    return (
      <DataRow
        element={{ ...element, type: 'textarea' }}
        formData={formData.toString()}
      />
    );
  }

  return null;
}
